import { ApiError, isTransientError } from './api-errors';

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number; // milliseconds
  maxDelay?: number;
  backoffMultiplier?: number;
  onRetry?: (attempt: number, error: Error) => void;
}

const DEFAULT_RETRY_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 30000, // 30 seconds
  backoffMultiplier: 2,
  onRetry: () => {}, // No-op by default
};

/**
 * Calculate exponential backoff delay
 * Attempt 1: 1s, Attempt 2: 2s, Attempt 3: 4s, etc.
 */
function getBackoffDelay(
  attempt: number,
  baseDelay: number,
  maxDelay: number,
  multiplier: number
): number {
  const delay = baseDelay * Math.pow(multiplier, attempt - 1);
  return Math.min(delay, maxDelay);
}

/**
 * Delay helper (sleep)
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry wrapper with exponential backoff
 * Only retries on transient errors
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const config = { ...DEFAULT_RETRY_OPTIONS, ...options };
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= config.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Check if error is transient
      const shouldRetry = isTransientError(error);

      if (!shouldRetry || attempt === config.maxRetries) {
        // Don't retry or max retries reached
        throw error;
      }

      // Call retry callback if provided
      config.onRetry(attempt, lastError);

      // Wait before retrying
      const waitTime = getBackoffDelay(
        attempt,
        config.baseDelay,
        config.maxDelay,
        config.backoffMultiplier
      ) + Math.random() * 1000;

      await delay(waitTime);
    }
  }

  throw lastError || new Error('Operation failed after retries');
}

/**
 * Wrapper for specific operation types
 */
export const createRetryableOperation = (
  operation: () => Promise<any>,
  operationType: 'mutation' | 'query' = 'mutation'
) => {
  const options = operationType === 'query'
    ? { maxRetries: 3, baseDelay: 500 }
    : { maxRetries: 3, baseDelay: 1000 };

  return retryWithBackoff(operation, options);
};
